import { useState, useEffect, memo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Building2, 
  Calendar, 
  FileText, 
  FileSignature, 
  Zap, 
  FileEdit,
  Palette,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Send,
  Upload,
  Mail,
  ExternalLink,
  Sparkles,
  X
} from "lucide-react";

const TOTAL_STEPS = 4;

const stepConfig = [
  { id: 1, title: "Business", icon: Building2, color: "text-primary" },
  { id: 2, title: "Scheduling", icon: Calendar, color: "text-blue-500" },
  { id: 3, title: "Documents", icon: FileText, color: "text-purple-500" },
  { id: 4, title: "Complete", icon: CheckCircle2, color: "text-green-500" }
];

// Step 1: Business Setup - EXTRACTED COMPONENT to prevent re-mounting
const Step1Business = memo(function Step1Business({ 
  businessName, 
  setBusinessName, 
  businessEmail, 
  setBusinessEmail, 
  notificationEmail, 
  setNotificationEmail 
}) {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Building2 className="w-8 h-8 text-primary" />
        </div>
        <h1 className="text-2xl font-bold text-foreground">Set up your business</h1>
        <p className="text-muted-foreground mt-2">This is what your clients will see.</p>
      </div>
      <div className="space-y-4 max-w-md mx-auto">
        <div>
          <Label htmlFor="businessName">Business Name *</Label>
          <Input
            id="businessName"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Your business name"
            className="mt-1.5"
            data-testid="onboarding-business-name"
          />
        </div>
        <div>
          <Label htmlFor="businessEmail">Business Email *</Label>
          <Input
            id="businessEmail"
            type="email"
            value={businessEmail}
            onChange={(e) => setBusinessEmail(e.target.value)}
            placeholder="contact@yourbusiness.com"
            className="mt-1.5"
            data-testid="onboarding-business-email"
          />
        </div>
        <div>
          <Label htmlFor="notificationEmail">Notification Email (optional)</Label>
          <Input
            id="notificationEmail"
            type="email"
            value={notificationEmail}
            onChange={(e) => setNotificationEmail(e.target.value)}
            placeholder="Where to send lead notifications"
            className="mt-1.5"
            data-testid="onboarding-notification-email"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Leave blank to use your business email
          </p>
        </div>
      </div>
    </div>
  );
});

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, token, setUser } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [creatingForm, setCreatingForm] = useState(false);
  
  // Step 1: Business
  const [businessName, setBusinessName] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [notificationEmail, setNotificationEmail] = useState("");
  
  // Step 2: Scheduling
  const [hasScheduling, setHasScheduling] = useState(null);
  const [calendlyLink, setCalendlyLink] = useState("");
  const [googleCalendarLink, setGoogleCalendarLink] = useState("");
  const [squareLink, setSquareLink] = useState("");
  
  // Step 3: Documents
  const [documentMethod, setDocumentMethod] = useState(null);
  const [documentLink, setDocumentLink] = useState("");
  const [documentEmail, setDocumentEmail] = useState("");
  
  // Step 4: Signing
  const [hasSigning, setHasSigning] = useState(null);
  const [signingLink, setSigningLink] = useState("");
  
  // Step 5: Form (moved up, skip Zapier)
  const [formPurpose, setFormPurpose] = useState(null);
  const [formTemplate, setFormTemplate] = useState(null);
  const [formName, setFormName] = useState("");
  const [createdFormId, setCreatedFormId] = useState(null);
  const [skippedFormCreation, setSkippedFormCreation] = useState(false);
  
  // Step 6: Workflow automations - deterministic toggles
  const [workflowToggles, setWorkflowToggles] = useState({
    auto_reply: false,          // Instantly reply to new leads
    send_intake: false,         // Send intake form when status → Won
    request_documents: false,   // Request documents when status → Awaiting Documents
    send_reminders: false,      // Send reminders for incomplete intakes
    completion_confirmation: false // Send confirmation when intake is complete
  });
  const [savingWorkflows, setSavingWorkflows] = useState(false);
  
  // Step 7: Branding
  const [userPlan, setUserPlan] = useState("starter");
  const [brandLogo, setBrandLogo] = useState(null);
  const [brandLogoPreview, setBrandLogoPreview] = useState(null);
  const [brandColor, setBrandColor] = useState("#166534"); // Default green
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Initialize with user data
  useEffect(() => {
    if (user) {
      setBusinessName(user.business_name || "");
      setBusinessEmail(user.email || "");
      setNotificationEmail(user.notification_email || "");
      setUserPlan(user.plan_id?.includes("professional") ? "professional" : "starter");
    }
  }, [user]);
  
  const skipOnboarding = async () => {
    try {
      await axios.post(`${API}/business/onboarding/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update user context
      setUser({ ...user, onboarding_completed: true });
      navigate("/dashboard");
    } catch (error) {
      console.error("Failed to skip onboarding:", error);
      navigate("/dashboard");
    }
  };
  
  const saveStep = async (stepNumber) => {
    setSaving(true);
    try {
      switch (stepNumber) {
        case 1:
          // Save business profile
          await axios.put(`${API}/business/onboarding`, {
            business_name: businessName,
            email: businessEmail,
            notification_email: notificationEmail || businessEmail
          }, { headers: { Authorization: `Bearer ${token}` } });
          break;
          
        case 2:
          // Save scheduling links to both legacy and workflow/settings (single source of truth)
          if (hasScheduling === true) {
            const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
            await axios.put(`${API}/business/scheduling-links`, {
              calendly: calendlyLink,
              google_calendar: googleCalendarLink,
              square_appointments: squareLink
            }, authHeaders);
            // Also save to IntegrationSettings so Settings page picks them up
            await axios.put(`${API}/workflow/settings`, {
              scheduling_link: calendlyLink || null,
              calendar_link: googleCalendarLink || null,
              square_link: squareLink || null
            }, authHeaders);
          }
          break;
          
        case 3:
          // Save document handling - REDESIGNED
          await axios.put(`${API}/business/document-handling`, {
            method: documentMethod || "none",
            email: documentMethod === "email" ? documentEmail : null
          }, { headers: { Authorization: `Bearer ${token}` } });
          break;
          
        default:
          break;
      }
    } catch (error) {
      console.error(`Failed to save step ${stepNumber}:`, error);
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };
  
  // CRITICAL: Save workflow automations - creates or deletes automations based on toggles
  const saveWorkflowAutomations = async () => {
    setSavingWorkflows(true);
    try {
      // Send workflow toggles to backend which will CREATE or DELETE automations
      await axios.post(`${API}/business/onboarding/workflows`, {
        toggles: workflowToggles,
        form_id: createdFormId // Pass form ID if created
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success("Workflow automations configured");
    } catch (error) {
      console.error("Failed to save workflow automations:", error);
      toast.error("Failed to configure workflows");
    } finally {
      setSavingWorkflows(false);
    }
  };
  
  // Handle logo file selection
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Logo must be less than 2MB");
        return;
      }
      setBrandLogo(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setBrandLogoPreview(previewUrl);
    }
  };
  
  // Upload logo to server
  const uploadLogo = async () => {
    if (!brandLogo) return;
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', brandLogo);
      
      await axios.post(`${API}/business/logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success("Logo uploaded successfully");
    } catch (error) {
      console.error("Failed to upload logo:", error);
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error("Failed to upload logo");
      }
    } finally {
      setUploadingLogo(false);
    }
  };
  
  const nextStep = async () => {
    await saveStep(currentStep);
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const createForm = async () => {
    if (!formName.trim()) {
      toast.error("Please enter a form name");
      return;
    }
    
    setCreatingForm(true);
    try {
      // Map form purpose and template to API values
      const mode = formPurpose === "lead" ? "pre_booking" : "post_booking";
      const templateMap = {
        "project": "project_based",
        "appointment": "appointment_based",
        "retainer": "retainer_based",
        "simple": "simple_inquiry"
      };
      const intakeTemplateMap = {
        "project": "project_intake",
        "appointment": "appointment_intake",
        "retainer": "detailed_client_intake",
        "simple": "simple_client_intake"
      };
      
      const template = mode === "pre_booking" 
        ? templateMap[formTemplate] || "simple_inquiry"
        : intakeTemplateMap[formTemplate] || "simple_client_intake";
      
      const response = await axios.post(`${API}/business/forms`, {
        name: formName,
        template_category: template,
        mode: mode
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setCreatedFormId(response.data.form.id);
      toast.success("Form created successfully!");
      setCurrentStep(4); // Move to complete step
    } catch (error) {
      console.error("Failed to create form:", error);
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail !== null) {
        // Handle structured error response (e.g., plan limit errors)
        toast.error(errorDetail.message || "Failed to create form");
      } else {
        toast.error(errorDetail || "Failed to create form");
      }
    } finally {
      setCreatingForm(false);
    }
  };

  const completeOnboarding = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/business/onboarding/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser({ ...user, onboarding_completed: true });
    } catch (error) {
      console.error("Failed to complete onboarding:", error);
    } finally {
      setSaving(false);
    }
  };
  
  // Progress bar - rendered inline to avoid re-creation
  const progressBar = (
    <div className="w-full max-w-3xl mx-auto mb-8 px-4">
      <div className="flex items-start justify-between">
        {stepConfig.map((step, index) => (
          <div key={step.id} className="flex flex-col items-center relative" style={{ flex: '1 1 0' }}>
            <div 
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all z-10 ${
                currentStep > step.id 
                  ? 'bg-primary text-white' 
                  : currentStep === step.id 
                    ? 'bg-primary text-white ring-4 ring-primary/20' 
                    : 'bg-gray-200 text-gray-500'
              }`}
            >
              {currentStep > step.id ? <CheckCircle2 className="w-4 h-4" /> : step.id}
            </div>
            {index < stepConfig.length - 1 && (
              <div 
                className={`absolute top-4 left-1/2 h-[2px] ${
                  currentStep > step.id ? 'bg-primary' : 'bg-gray-200'
                }`}
                style={{ width: '100%', transform: 'translateX(0)' }}
              />
            )}
            <span 
              className={`mt-1.5 text-[11px] text-center whitespace-nowrap hidden sm:block ${
                currentStep === step.id 
                  ? 'text-primary font-semibold' 
                  : currentStep > step.id
                    ? 'text-primary/70'
                    : 'text-muted-foreground'
              }`}
            >
              {step.title}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
  
  // Render current step - use memoized components for steps with inputs
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1Business
            businessName={businessName}
            setBusinessName={setBusinessName}
            businessEmail={businessEmail}
            setBusinessEmail={setBusinessEmail}
            notificationEmail={notificationEmail}
            setNotificationEmail={setNotificationEmail}
          />
        );
        
      case 2:
        // Step 2: Scheduling
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-blue-500" />
              </div>
              <h1 className="text-2xl font-bold text-foreground">Do you use a scheduling tool?</h1>
              <p className="text-muted-foreground mt-2">Add it once — we'll include it in your emails automatically.</p>
            </div>
            {hasScheduling === null ? (
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <Button 
                  variant="outline" 
                  className="flex-1 h-auto py-4 flex flex-col gap-2"
                  onClick={() => setHasScheduling(true)}
                  data-testid="onboarding-has-scheduling"
                >
                  <Calendar className="w-6 h-6 text-blue-500" />
                  <span>Yes, I have one</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 h-auto py-4 flex flex-col gap-2"
                  onClick={() => { setHasScheduling(false); nextStep(); }}
                  data-testid="onboarding-skip-scheduling"
                >
                  <ArrowRight className="w-6 h-6 text-muted-foreground" />
                  <span>Skip for now</span>
                </Button>
              </div>
            ) : (
              <div className="space-y-4 max-w-md mx-auto">
                <h2 className="font-medium text-center mb-4">Add your scheduling link</h2>
                <div>
                  <Label htmlFor="calendly">Calendly</Label>
                  <Input
                    id="calendly"
                    value={calendlyLink}
                    onChange={(e) => setCalendlyLink(e.target.value)}
                    placeholder="https://calendly.com/your-link"
                    className="mt-1.5"
                    data-testid="onboarding-calendly"
                  />
                </div>
                <div>
                  <Label htmlFor="googleCalendar">Google Calendar</Label>
                  <Input
                    id="googleCalendar"
                    value={googleCalendarLink}
                    onChange={(e) => setGoogleCalendarLink(e.target.value)}
                    placeholder="https://calendar.google.com/..."
                    className="mt-1.5"
                    data-testid="onboarding-google-calendar"
                  />
                </div>
                <div>
                  <Label htmlFor="square">Square Appointments</Label>
                  <Input
                    id="square"
                    value={squareLink}
                    onChange={(e) => setSquareLink(e.target.value)}
                    placeholder="https://squareup.com/..."
                    className="mt-1.5"
                    data-testid="onboarding-square"
                  />
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Paste any booking link you already use.
                </p>
                <Button 
                  variant="ghost" 
                  className="w-full mt-2" 
                  onClick={() => setHasScheduling(null)}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              </div>
            )}
          </div>
        );
        
      case 3:
        // Step 3: Documents - REDESIGNED
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-purple-500" />
              </div>
              <h1 className="text-2xl font-bold text-foreground">How do clients send you documents?</h1>
              <p className="text-muted-foreground mt-2">Choose your preferred method for document collection.</p>
            </div>
            <div className="flex flex-col gap-3 max-w-md mx-auto">
              <button 
                className={`h-auto py-4 px-4 text-left border-2 rounded-xl transition-all ${
                  documentMethod === "portal" 
                    ? 'border-primary bg-primary/5' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setDocumentMethod("portal")}
                data-testid="onboarding-doc-portal"
              >
                <div className="flex items-center gap-3">
                  <Upload className="w-5 h-5 text-primary" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">HeyIntake Upload Portal</span>
                      <Badge className="bg-primary/10 text-primary text-xs">Recommended</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">Clients upload documents securely inside HeyIntake</p>
                  </div>
                </div>
              </button>
              <button 
                className={`h-auto py-4 px-4 text-left border-2 rounded-xl transition-all ${
                  documentMethod === "client_specific" 
                    ? 'border-primary bg-primary/5' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setDocumentMethod("client_specific")}
                data-testid="onboarding-doc-external"
              >
                <div className="flex items-center gap-3">
                  <ExternalLink className="w-5 h-5 text-orange-500" />
                  <div>
                    <span className="font-medium">External Upload Links</span>
                    <p className="text-sm text-muted-foreground">Provide custom links (Google Drive, Dropbox, etc.) per client</p>
                  </div>
                </div>
              </button>
              <button 
                className={`h-auto py-4 px-4 text-left border-2 rounded-xl transition-all ${
                  documentMethod === "email" 
                    ? 'border-primary bg-primary/5' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setDocumentMethod("email")}
                data-testid="onboarding-doc-email"
              >
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-500" />
                  <div>
                    <span className="font-medium">Business Email</span>
                    <p className="text-sm text-muted-foreground">Clients send documents by replying to your email</p>
                  </div>
                </div>
              </button>
              <button 
                className={`h-auto py-4 px-4 text-left border-2 rounded-xl transition-all ${
                  documentMethod === "none" 
                    ? 'border-primary bg-primary/5' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setDocumentMethod("none")}
                data-testid="onboarding-doc-none"
              >
                <div className="flex items-center gap-3">
                  <X className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <span className="font-medium">Not Needed</span>
                    <p className="text-sm text-muted-foreground">You don't collect documents from clients</p>
                  </div>
                </div>
              </button>
            </div>
            
            {documentMethod === "email" && (
              <div className="space-y-3 max-w-md mx-auto mt-4 pl-4 border-l-2 border-primary/20">
                <Label htmlFor="documentEmail">Document Submission Email *</Label>
                <Input
                  id="documentEmail"
                  type="email"
                  value={documentEmail}
                  onChange={(e) => setDocumentEmail(e.target.value)}
                  placeholder="documents@yourbusiness.com"
                  data-testid="onboarding-document-email"
                />
              </div>
            )}
            
            {documentMethod === "portal" && (
              <div className="max-w-md mx-auto mt-4 pl-4 border-l-2 border-primary/20">
                <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                  <p className="text-sm text-green-700">
                    Clients will upload documents directly through your secure HeyIntake portal.
                  </p>
                </div>
              </div>
            )}
            
            {documentMethod === "client_specific" && (
              <div className="max-w-md mx-auto mt-4 pl-4 border-l-2 border-primary/20">
                <div className="bg-orange-50 border border-orange-100 rounded-lg p-3">
                  <p className="text-sm text-orange-700">
                    You'll add a unique upload link (Google Drive, Dropbox, etc.) for each client in the Submissions tab.
                  </p>
                </div>
              </div>
            )}
          </div>
        );
        
      case 4:
        // Step 4: Complete - Updated with new CTAs
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <CheckCircle2 className="w-10 h-10 text-green-500" />
              </motion.div>
              <h1 className="text-2xl font-bold text-foreground">You're all set!</h1>
              <p className="text-muted-foreground mt-2">Your account is ready. Start capturing leads.</p>
            </div>
            
            <div className="flex flex-col gap-3 max-w-md mx-auto">
              {/* Primary CTA: Go to Dashboard */}
              <Button
                size="lg"
                className="w-full gap-2 rounded-full"
                onClick={async () => {
                  await completeOnboarding();
                  navigate("/dashboard");
                }}
                disabled={saving}
                data-testid="onboarding-go-dashboard"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Go to Dashboard
              </Button>
              
              {/* Secondary CTA: Create your first form */}
              <Button
                variant="outline"
                size="lg"
                className="w-full gap-2 rounded-full"
                onClick={async () => {
                  await completeOnboarding();
                  navigate("/form-builder");
                }}
                disabled={saving}
                data-testid="onboarding-create-form"
              >
                <FileEdit className="w-4 h-4" />
                Create your first form
              </Button>
            </div>
            
            <p className="text-xs text-center text-muted-foreground mt-4 max-w-sm mx-auto">
              Your dashboard checklist will guide you through the next steps.
            </p>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  // Check if current step can proceed
  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return businessName.trim() && businessEmail.trim();
      case 2:
        return true; // Optional step
      case 3:
        return documentMethod !== null;
      case 4:
        return true; // Complete
      default:
        return true;
    }
  };
  
  // Show continue button logic
  const showContinueButton = () => {
    // Step 4 (Complete) has its own buttons
    if (currentStep === 4) return false;
    
    // For steps with sub-choices, don't show Continue until choice is made
    if (currentStep === 2 && hasScheduling === null) return false;
    if (currentStep === 3 && documentMethod === null) return false;
    
    return true;
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      {/* Header */}
      <header className="px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-lg">HeyIntake</span>
        </div>
        <Button 
          variant="ghost" 
          className="text-muted-foreground"
          onClick={skipOnboarding}
          data-testid="onboarding-skip"
        >
          Skip for now
        </Button>
      </header>
      
      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        {progressBar}
        
        <Card className="w-full max-w-xl bg-white rounded-2xl shadow-lg border-0 p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </Card>
        
        {/* Navigation Buttons */}
        {showContinueButton() && (
          <div className="flex items-center gap-4 mt-6">
            {currentStep > 1 && (
              <Button variant="ghost" onClick={prevStep} disabled={saving}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            )}
            <Button
              onClick={nextStep}
              disabled={saving || !canProceed()}
              className="min-w-[120px]"
              data-testid="onboarding-continue"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
